import React from 'react';

/**
 * Footer Component
 * Shared footer used across all pages
 * Colors: #fcbe37 (Gold) & #296481 (Blue)
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-school-blue text-white pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-10 md:pb-12 border-t border-blue-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-12 sm:mb-16 md:mb-20">
          <div className="col-span-1 sm:col-span-2 md:col-span-1">
             <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                 <div className="bg-white p-1.5 sm:p-2 rounded-full shadow-lg">
                    <img src="/assets/images/school_logo.png" alt="P.S. 155 William Paca School logo" className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 object-contain" />
                 </div>
                 <span className="font-bold text-lg sm:text-xl leading-tight tracking-wide">P.S. 155 M<br/>William Paca</span>
             </div>
             <p className="text-blue-100 text-sm sm:text-base leading-relaxed opacity-90">
                Empowering students to become lifelong learners and leaders in their community through empathy, equity, and excellence.
             </p>
          </div>

          <div>
            <h3 className="font-bold text-base sm:text-lg mb-4 sm:mb-6 md:mb-8 text-school-gold-on-blue uppercase tracking-wider text-xs sm:text-sm">Quick Links</h3>
            <ul className="space-y-2 sm:space-y-3 md:space-y-4 text-blue-100 text-sm sm:text-base">
              <li><a href="/about" className="hover:text-white hover:translate-x-1 transition-all inline-block">About Us</a></li>
              <li><a href="/initiatives" className="hover:text-white hover:translate-x-1 transition-all inline-block">Our Programs</a></li>
              <li><a href="#staff" className="hover:text-white hover:translate-x-1 transition-all inline-block">Staff Directory</a></li>
              <li><a href="/calendar" className="hover:text-white hover:translate-x-1 transition-all inline-block">Calendar</a></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-base sm:text-lg mb-4 sm:mb-6 md:mb-8 text-school-gold-on-blue uppercase tracking-wider text-xs sm:text-sm">Contact</h3>
            <ul className="space-y-3 sm:space-y-4 text-blue-100 text-sm sm:text-base">
              <li className="flex items-start gap-2 sm:gap-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mt-0.5 flex-shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                <span className="leading-snug">319 East 117th Street<br/>New York, NY 10035</span>
              </li>
              <li className="flex items-center gap-2 sm:gap-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                <span>(212) 555-0155</span>
              </li>
            </ul>
          </div>

          <div className="col-span-1 sm:col-span-2 md:col-span-1">
            <h3 className="font-bold text-base sm:text-lg mb-4 sm:mb-6 md:mb-8 text-school-gold-on-blue uppercase tracking-wider text-xs sm:text-sm">Stay Connected</h3>
            <p className="text-blue-100 text-xs sm:text-sm mb-4 sm:mb-6 opacity-90 leading-relaxed">Sign up for our newsletter to get the latest updates delivered to your inbox.</p>
            <form className="flex gap-2 sm:gap-3">
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <input id="newsletter-email" type="email" autoComplete="email" placeholder="Enter your email" aria-required="true" className="bg-blue-900/50 border border-blue-700 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-blue-200 w-full focus:ring-2 focus:ring-school-gold outline-none transition-all" />
                <button type="button" aria-label="Subscribe to newsletter" className="bg-school-gold text-school-blue-dark p-2 sm:p-3 rounded-lg hover:bg-yellow-500 transition-colors shadow-lg flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                </button>
            </form>
          </div>
        </div>

        <div className="border-t border-blue-800/50 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6 text-xs sm:text-sm text-blue-100">
            <p className="text-center md:text-left">&copy; {currentYear} P.S. 155 William Paca School. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8">
                <a href="#privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
                <a href="#accessibility" className="hover:text-white transition-colors whitespace-nowrap">Accessibility</a>
                <a href="#sitemap" className="hover:text-white transition-colors whitespace-nowrap">Sitemap</a>
            </div>
        </div>

        {/* NexSav Credit */}
        <div className="mt-4 pt-4 border-t border-blue-800/50">
          <a
            href="https://nexsav.net"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-blue-100 hover:text-white transition-all duration-300 group"
          >
            <img
              src="https://nexsav.net/static/media/logo.7ef8f9a8ca3ec946bd31.png"
              alt="NexSav Logo"
              className="h-5 opacity-60 group-hover:opacity-90 transition-opacity duration-300"
            />
            <span className="text-xs font-medium tracking-wide">
              Website designed & developed by <span className="font-semibold">NexSav</span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
